<script setup lang="ts">
import { useInfiniteScroll } from '@vueuse/core';
import { computed, reactive, useTemplateRef } from 'vue';

import type { FiscalShiftAction } from '@/entities/fiscalization';
import type { LocationAttentionRow } from '@/entities/location';
import type { UseFiscalShiftStatusReturn } from '@/features/order-fiscalization';
import type { Temporal } from '@shared/lib/date-time';

import { useAuthStore } from '@/entities/auth';
import { LocationCodeBadge, organizationCode } from '@/entities/location';
import { OrganizationSwitchAccess } from '@/entities/organization';
import { OrderFiscalShiftSummary } from '@/features/order-fiscalization';
import { AppInput } from '@/shared/ui';
import { IconChevronRight } from '@/shared/ui/icons';

import type { PlaqueGroup, PlaqueRow, PlaqueTone } from '../lib/location-plaque-state';

import { locationShiftCaption, locationShiftDotTone, withoutOrganizationPrefix } from '../lib/location-plaque-state';
import { useLocationSwitcher } from '../model/use-location-switcher';
import { type SwitchableOrganization, useOrganizationSwitcher } from '../model/use-organization-switcher';

/** Содержимое плашки локации: смена, «Нужно решить», смена локации и организации. */
const props = defineProps<{
  status: UseFiscalShiftStatusReturn;
  canSeeShift: boolean;
  groups: PlaqueGroup[];
  byLocation: Map<string, LocationAttentionRow>;
  now: Temporal.Instant;
  kassaLocationId: string | null;
}>();
const emit = defineEmits<{ close: []; shiftAction: [action: Exclude<FiscalShiftAction, null>] }>();

const auth = useAuthStore();
const { currentLocation, availableLocations, hasMultipleLocations, selectLocation } = useLocationSwitcher();
const switcher = reactive(useOrganizationSwitcher());
const { isLoading, shiftState } = props.status;

const organizationTitle = computed(() => auth.currentOrganization?.title ?? '');
const hasLocations = computed(() => auth.availableLocations.length > 0);
const otherLocations = computed(() =>
  availableLocations.value
    .filter((location) => location.id !== currentLocation.value?.id)
    .map((location) => {
      const row = props.byLocation.get(location.id);

      return {
        location,
        dot: locationShiftDotTone(row, props.now),
        caption: locationShiftCaption(row, props.now, location.timezone),
      };
    }),
);
const kassaLocation = computed(() => auth.availableLocations.find((location) => location.id === props.kassaLocationId));
const showShift = computed(
  () => props.canSeeShift && currentLocation.value !== null && (shiftState.value !== null || isLoading.value),
);

const toneDot: Record<PlaqueRow['tone'] | PlaqueTone, string> = {
  danger: 'bg-(--danger)',
  info: 'bg-(--accent)',
  success: 'bg-(--success)',
  warning: 'bg-(--warning)',
};

const shortTitle = (title: string): string => withoutOrganizationPrefix(title, organizationTitle.value);

function onShiftAction(action: Exclude<FiscalShiftAction, null>): void {
  emit('shiftAction', action);
  emit('close');
}

async function switchTo(locationId: string): Promise<void> {
  const target = auth.availableLocations.find((location) => location.id === locationId);
  emit('close');
  if (target) await selectLocation(target);
}

const organizationList = useTemplateRef<HTMLElement>('organizationList');
useInfiniteScroll(organizationList, () => switcher.loadNextPage(), {
  distance: 80,
  canLoadMore: () => switcher.canLoadMore,
});

function onOrganizationsToggle(event: Event): void {
  switcher.open = (event.target as HTMLDetailsElement).open;
}

async function switchOrganization(organization: SwitchableOrganization): Promise<void> {
  emit('close');
  await switcher.select(organization);
}
</script>

<template>
  <div class="flex flex-col gap-4 p-4 text-(--text-primary)">
    <!-- Код и цвет уже на плашке, к которой привязана панель: здесь только название. -->
    <div class="min-w-0">
      <p class="truncate text-xs text-(--text-secondary)">{{ organizationTitle }}</p>
      <p class="truncate font-semibold">
        {{ currentLocation ? shortTitle(currentLocation.title) : 'Локаций пока нет' }}
      </p>
    </div>

    <OrderFiscalShiftSummary
      v-if="showShift && currentLocation"
      :status="status"
      :location-id="currentLocation.id"
      @action="onShiftAction"
    />

    <section
      v-if="groups.length > 0"
      class="flex flex-col gap-3 border-t border-(--border) pt-3"
    >
      <h3 class="text-xs text-(--text-secondary)">Нужно решить</h3>
      <div
        v-for="group in groups"
        :key="group.id"
        class="flex flex-col gap-1"
      >
        <p class="text-xs text-(--text-secondary)">{{ shortTitle(group.title) }}</p>
        <template
          v-for="row in group.rows"
          :key="row.id"
        >
          <RouterLink
            v-if="row.target.kind === 'route'"
            :to="row.target.to"
            class="flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm hover:bg-(--surface-hover)"
            @click="emit('close')"
          >
            <span
              class="h-2 w-2 shrink-0 rounded-full"
              :class="toneDot[row.tone]"
              aria-hidden="true"
            />
            <span class="min-w-0 flex-1">{{ row.text }}</span>
            <IconChevronRight
              :width="12"
              :height="12"
            />
          </RouterLink>
          <button
            v-else-if="row.target.kind === 'switch'"
            type="button"
            class="flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-lg px-2 text-left text-sm hover:bg-(--surface-hover)"
            @click="switchTo(row.target.locationId)"
          >
            <span
              class="h-2 w-2 shrink-0 rounded-full"
              :class="toneDot[row.tone]"
              aria-hidden="true"
            />
            <span class="min-w-0 flex-1">{{ row.text }}</span>
            <span class="text-xs text-(--text-secondary)">Перейти</span>
          </button>
          <p
            v-else
            class="flex min-h-11 items-center gap-2 px-2 text-sm"
          >
            <span
              class="h-2 w-2 shrink-0 rounded-full"
              :class="toneDot[row.tone]"
              aria-hidden="true"
            />
            {{ row.text }}
          </p>
        </template>
      </div>
    </section>

    <section
      v-if="hasMultipleLocations"
      class="flex flex-col gap-1 border-t border-(--border) pt-3"
    >
      <h3 class="text-xs text-(--text-secondary)">Другие локации</h3>
      <button
        v-for="{ location, dot, caption } in otherLocations"
        :key="location.id"
        type="button"
        class="flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-lg px-2 text-left hover:bg-(--surface-hover)"
        @click="switchTo(location.id)"
      >
        <!-- Полное название рядом: код локации здесь лишний, точка смены держит место и без статуса. -->
        <span
          class="h-2 w-2 shrink-0 rounded-full"
          :class="dot ? toneDot[dot] : ''"
          aria-hidden="true"
        />
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm">{{ shortTitle(location.title) }}</span>
          <span
            v-if="caption"
            class="block truncate text-xs text-(--text-secondary)"
          >
            {{ caption }}
          </span>
        </span>
      </button>
    </section>

    <section
      v-if="!hasLocations"
      class="flex flex-col gap-2 border-t border-(--border) pt-3 text-sm"
    >
      <RouterLink
        v-if="auth.hasPermission('locations.manage')"
        :to="{ name: 'business-settings.locations' }"
        class="text-(--accent) hover:underline"
        @click="emit('close')"
      >
        Создать локацию
      </RouterLink>
    </section>

    <details
      v-if="switcher.visible"
      class="group border-t border-(--border) pt-3"
      @toggle="onOrganizationsToggle"
    >
      <summary
        class="flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-lg px-2 text-sm hover:bg-(--surface-hover) [&::-webkit-details-marker]:hidden"
      >
        <span class="flex-1 text-(--text-secondary)">Организация</span>
        <span class="min-w-0 truncate">{{ organizationTitle }}</span>
        <IconChevronRight
          class="shrink-0 text-(--text-secondary) transition-transform group-open:rotate-90 motion-reduce:transition-none"
          :width="7"
          :height="12"
        />
      </summary>
      <div class="mt-1 flex flex-col gap-2">
        <AppInput
          v-if="switcher.showSearch"
          v-model="switcher.search"
          size="sm"
          placeholder="Найти организацию"
        />
        <div
          ref="organizationList"
          class="flex max-h-72 flex-col gap-1 overflow-y-auto"
        >
          <button
            v-for="organization in switcher.organizations"
            :key="organization.id"
            type="button"
            class="flex min-h-11 w-full items-center gap-3 rounded-lg px-2 text-left text-sm"
            :class="
              organization.access === OrganizationSwitchAccess.NoOwner
                ? 'cursor-default opacity-50'
                : 'cursor-pointer hover:bg-(--surface-hover)'
            "
            :disabled="organization.access === OrganizationSwitchAccess.NoOwner"
            @click="switchOrganization(organization)"
          >
            <LocationCodeBadge
              :code="organizationCode(organization.title)"
              size="md"
            />
            <span class="min-w-0 flex-1">
              <span class="block truncate">{{ organization.title }}</span>
              <span
                v-if="organization.access === OrganizationSwitchAccess.NoOwner"
                class="block truncate text-xs text-(--text-secondary)"
              >
                Нет владельца
              </span>
            </span>
          </button>
          <p
            v-if="!switcher.isLoading && switcher.organizations.length === 0"
            class="px-2 py-3 text-sm text-(--text-secondary)"
          >
            Ничего не найдено
          </p>
        </div>
      </div>
    </details>

    <p
      v-if="kassaLocation"
      class="border-t border-(--border) pt-3 text-xs text-(--text-secondary)"
    >
      Касса этого компьютера: {{ shortTitle(kassaLocation.title) }}
    </p>
  </div>
</template>
